// Drawing controls in a single row: color, opacity, size, and undo/redo/clear.

import React from 'react';
import {
  BrushIcon,
  DropletsIcon,
  Redo2Icon,
  Trash2Icon,
  Undo2Icon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { getColorStop } from '@/styles/colorStops';
import { cn } from '@/utilities/cn';
import { DrawingColorPicker } from './DrawingColorPicker';
import type { DrawingAction, DrawingState } from './types';

// Strokes store a logical pixel size that perfect-freehand consumes directly,
// but that 8–120 range is meaningless to players, so the UI presents a 1–100
// scale and maps to/from pixels only at this boundary.
const MIN_THICKNESS = 8;
const MAX_THICKNESS = 120;
const SIZE_MIN = 1;
const SIZE_MAX = 100;

const toSizeDisplay = (pixels: number): number =>
  Math.round(
    SIZE_MIN +
      ((pixels - MIN_THICKNESS) / (MAX_THICKNESS - MIN_THICKNESS)) *
        (SIZE_MAX - SIZE_MIN)
  );

const toSizePixels = (display: number): number =>
  Math.round(
    MIN_THICKNESS +
      ((display - SIZE_MIN) / (SIZE_MAX - SIZE_MIN)) *
        (MAX_THICKNESS - MIN_THICKNESS)
  );

interface DrawingToolbarProps {
  state: DrawingState;
  dispatch: React.Dispatch<DrawingAction>;
}

const IconButtonWithTooltip: React.FC<{
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, disabled, onClick, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="outline"
        size="icon-lg"
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

// Editable numeric readout: shows the current value at all times and accepts a
// typed value, clamped to [min, max] on commit. A local draft lets the field
// hold partial input (e.g. an empty box) without fighting the canonical value.
const ToolbarNumberField: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  suffix?: string;
  className?: string;
  wrapperClassName?: string;
  onCommit: (value: number) => void;
}> = ({
  label,
  value,
  min,
  max,
  suffix,
  className,
  wrapperClassName,
  onCommit,
}) => {
  const [draft, setDraft] = React.useState<string | null>(null);

  const commit = (raw: string) => {
    const parsed = parseInt(raw, 10);
    if (!Number.isNaN(parsed)) {
      onCommit(Math.min(max, Math.max(min, parsed)));
    }
    setDraft(null);
  };

  return (
    <div className={cn('flex items-center gap-0.5', wrapperClassName)}>
      <Input
        type="text"
        inputMode="numeric"
        aria-label={label}
        value={draft ?? String(value)}
        onChange={(event) =>
          setDraft(event.target.value.replace(/[^0-9]/g, ''))
        }
        onBlur={(event) => commit(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            commit(event.currentTarget.value);
            event.currentTarget.blur();
          } else if (event.key === 'Escape') {
            setDraft(null);
            event.currentTarget.blur();
          }
        }}
        className={cn('px-1 text-center text-sm tabular-nums', className)}
      />
      {suffix && (
        <span className="text-sm text-muted-foreground">{suffix}</span>
      )}
    </div>
  );
};

export const DrawingToolbar: React.FC<DrawingToolbarProps> = ({
  state,
  dispatch,
}) => {
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const activeColor = getColorStop(state.colorFamily, state.colorStop);
  const hasStrokes = state.strokes.length > 0;
  const opacityPercent = Math.round(state.opacity * 100);
  const sizeDisplay = toSizeDisplay(state.thickness);

  return (
    <TooltipProvider delayDuration={300}>
      {/* @container drives the degrade order as the toolbar narrows to its
          canvas: numeric readouts drop first, then the divider, then the
          slider labels swap to icons — sliders and history always survive. */}
      <div className="@container flex flex-wrap items-center justify-center gap-3 rounded-lg bg-card p-2.5 ring-1 ring-foreground/10">
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon-lg"
              aria-label="Color"
              className="ring-2 ring-foreground/25 ring-inset"
              style={{ backgroundColor: activeColor }}
            />
          </PopoverTrigger>
          <PopoverContent className="w-fit" align="start">
            <DrawingColorPicker
              family={state.colorFamily}
              stop={state.colorStop}
              onChange={(family, stop) => {
                dispatch({ type: 'SET_COLOR', family, stop });
                setPickerOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>

        <div className="flex items-center gap-2">
          <span className="hidden text-sm text-muted-foreground @[26rem]:inline">
            Opacity
          </span>
          <DropletsIcon
            aria-label="Opacity"
            className="size-4 shrink-0 text-muted-foreground @[26rem]:hidden"
          />
          <Slider
            className="w-20"
            min={5}
            max={100}
            value={[opacityPercent]}
            onValueChange={([value]) =>
              dispatch({ type: 'SET_OPACITY', opacity: value / 100 })
            }
          />
          <ToolbarNumberField
            label="Opacity percent"
            value={opacityPercent}
            min={5}
            max={100}
            suffix="%"
            className="w-12"
            wrapperClassName="hidden @[30rem]:flex"
            onCommit={(value) =>
              dispatch({ type: 'SET_OPACITY', opacity: value / 100 })
            }
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="hidden text-sm text-muted-foreground @[26rem]:inline">
            Size
          </span>
          <BrushIcon
            aria-label="Size"
            className="size-4 shrink-0 text-muted-foreground @[26rem]:hidden"
          />
          <Slider
            className="w-20"
            min={SIZE_MIN}
            max={SIZE_MAX}
            value={[sizeDisplay]}
            onValueChange={([value]) =>
              dispatch({
                type: 'SET_THICKNESS',
                thickness: toSizePixels(value),
              })
            }
          />
          <ToolbarNumberField
            label="Size"
            value={sizeDisplay}
            min={SIZE_MIN}
            max={SIZE_MAX}
            className="w-12"
            wrapperClassName="hidden @[30rem]:flex"
            onCommit={(value) =>
              dispatch({
                type: 'SET_THICKNESS',
                thickness: toSizePixels(value),
              })
            }
          />
        </div>

        {/* Divider separates brush settings from the history actions; only
            meaningful while everything still fits on a single row */}
        <div className="hidden h-8 w-px bg-foreground/10 @[34rem]:block" />

        <div className="flex items-center gap-1.5">
          <IconButtonWithTooltip
            label="Undo"
            disabled={!hasStrokes}
            onClick={() => dispatch({ type: 'UNDO' })}
          >
            <Undo2Icon className="size-5" />
          </IconButtonWithTooltip>
          <IconButtonWithTooltip
            label="Redo"
            disabled={state.redoStack.length === 0}
            onClick={() => dispatch({ type: 'REDO' })}
          >
            <Redo2Icon className="size-5" />
          </IconButtonWithTooltip>
          <IconButtonWithTooltip
            label="Clear"
            disabled={!hasStrokes}
            onClick={() => dispatch({ type: 'CLEAR' })}
          >
            <Trash2Icon className="size-5" />
          </IconButtonWithTooltip>
        </div>
      </div>
    </TooltipProvider>
  );
};
